import {
  isValidPhone,
  normalizePhoneNumber,
} from '@brightdrop/localization-core';
import type { Address } from '@gm-commercial/location-model';
import {
  type InvitationStatus,
  type Invite,
  Role,
  type User,
} from '@gm-commercial/profile-model';
import type {
  AddressDto,
  HierarchyObjectView,
  ProfileResponse,
} from '@gm-commercial/rtk-clients/users/types';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { mapApiResponseToAddress } from '~/common/mappers/common.mappers';
import type {
  ActivationStatus,
  UserSupportedLocale,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type { UserFormFields } from '~/common/models/user.model';
import {
  lowercaseLiteral,
  uppercaseLiteral,
} from '~/common/utils/common.utils';

import type {
  AddUserFormPayload,
  UpdateUserFormPayload,
} from '../../usersSlice';

type UserApiRequestPayload = {
  role: Role;
  organizationId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  fleetId: string | null;
  phoneNumber?: string;
  status?: Uppercase<ActivationStatus>;
  userPreferences?: {
    locale?: UserSupportedLocale | null;
    timeZone?: string | null;
  };
};

const getDisplayName = ({
  displayName,
  firstName,
  lastName,
}: {
  displayName: unknown;
  firstName: unknown;
  lastName: unknown;
}): string => {
  const validatedDisplayName =
    displayName && typeof displayName === 'string' ? displayName.trim() : null;
  const validatedFirstName =
    firstName && typeof firstName === 'string' && firstName.trim()
      ? firstName.trim()
      : '';
  const validatedLastName =
    lastName && typeof lastName === 'string' && lastName.trim()
      ? lastName.trim()
      : '';
  return (
    validatedDisplayName ||
    (validatedFirstName && validatedLastName
      ? `${validatedFirstName} ${validatedLastName}`
      : DEFAULT_EMPTY_VALUE)
  );
};

export const mapApiResponseInviteToUserInvite = (
  apiInvite: Invite | undefined
) => {
  if (!apiInvite) {
    return undefined;
  }
  return apiInvite;
};

export const mapInvitationStatusToBackend = (
  invitationStatus: InvitationStatus
): string => invitationStatus;

/**
 * The API sends the enum key (`ORG_MANAGER`); `User` models the label (`Account Admin`).
 *
 * A key the app does not know, and an absent role, leave `User.role` with no value it can hold.
 * The gap is absorbed here rather than widened into the model: `Profile extends User`, and
 * permission gating reads a signed-in profile's role as always present.
 */
const mapRole = (role: ProfileResponse['role']): Role =>
  (typeof role === 'string'
    ? Role[role as keyof typeof Role]
    : DEFAULT_EMPTY_VALUE) as Role;

/** The API sends the status uppercased; `ActivationStatus` spells it lowercase. */
const mapStatus = (status: ProfileResponse['status']): ActivationStatus =>
  (typeof status === 'string'
    ? lowercaseLiteral(status)
    : undefined) as ActivationStatus;

/** `AddressDto` makes every line optional, while `Address` requires four of them. */
const mapAddress = (address: AddressDto): Address =>
  mapApiResponseToAddress({
    line1: address.line1 ?? '',
    line2: address.line2,
    city: address.city ?? '',
    state: address.state,
    postalCode: address.postalCode ?? '',
    country: address.country ?? '',
  });

const mapScopeEntity = (
  entity?: HierarchyObjectView
): NonNullable<User['organization']> | undefined =>
  entity ? { id: entity.id ?? '', name: entity.name ?? '' } : undefined;

/**
 * `hub` is absent from `ProfileResponse` yet read by the user edit and assign forms, so it is
 * carried across rather than dropped while the spec and those consumers disagree.
 */
const readHub = (rawResult: ProfileResponse): User['hub'] =>
  mapScopeEntity((rawResult as { hub?: HierarchyObjectView }).hub);

/**
 * Maps a profile phone for Redux as E.164 when the normalizer can read it, else the placeholder.
 * Not gated on the localization feature flag; flag off display still uses legacy formatters.
 */
export const mapProfilePhoneNumber = (raw?: string | null): string => {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return DEFAULT_EMPTY_VALUE;

  const normalized = normalizePhoneNumber(trimmed);
  if (!normalized || !isValidPhone(normalized)) return DEFAULT_EMPTY_VALUE;

  return normalized;
};

/** E.164 for forms and models; empty string when the profile mapper would use the placeholder. */
export const mapPhoneToFormValue = (raw?: string | null): string => {
  const mapped = mapProfilePhoneNumber(raw);
  return mapped === DEFAULT_EMPTY_VALUE ? '' : mapped;
};

/**
 * Maps a profile as the users API returns it onto the `User` the app renders.
 *
 * Every field is listed rather than spread: `ProfileResponse` also carries keys the app does not
 * model — `appSettings`, `cdiProfileId`, `eligibleAssetTypes`, audit stamps — and a spread would
 * put them in the store under a type that denies they exist. `activationDate` goes the other way:
 * the model declares it, no profile endpoint returns it, and nothing reads it.
 */

export const mapApiResponseToUser = (rawResult: ProfileResponse): User => {
  const { displayName, firstName, lastName } = rawResult;
  return {
    id: rawResult.id ?? '',
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    displayName: displayName ?? '',
    name: getDisplayName({ displayName, firstName, lastName }),
    email: rawResult.email ?? '',
    role: mapRole(rawResult.role),
    status: mapStatus(rawResult.status),
    phoneNumber: mapProfilePhoneNumber(rawResult.phoneNumber),
    address: rawResult.address ? mapAddress(rawResult.address) : undefined,
    organization: mapScopeEntity(rawResult.organization),
    hub: readHub(rawResult),
    fleet: mapScopeEntity(rawResult.fleet),
    invite: mapApiResponseInviteToUserInvite(rawResult.invite),
    creationDate: rawResult.creationDate,
    lastSignedInDate: rawResult.lastSignedInDate,
    userPreferences: rawResult.userPreferences
      ? {
          ...rawResult.userPreferences,
          locale: rawResult.userPreferences.locale as UserSupportedLocale,
        }
      : undefined,
    effectiveUserPreferences: rawResult.effectiveUserPreferences,
    migrated: rawResult.migrated,
    viewedAppSpaceCodes: rawResult.viewedAppSpaceCodes,
  };
};

/**
 * Reads the profile out of a users-API envelope.
 *
 * Throws rather than mapping an absent result, so a 200 carrying no profile is recorded as an API
 * error by the slice instead of surfacing as a destructuring `TypeError` from inside the adapter.
 */
export const readProfileResponse = (response: unknown): ProfileResponse => {
  if (response && typeof response === 'object' && 'result' in response) {
    const { result } = response as { result: unknown };
    if (result && typeof result === 'object') {
      return result as ProfileResponse;
    }
  }
  throw new BDError('Unexpected user response', {
    type: BDAppErrorType.VALIDATION,
    data: response,
  });
};

export const mapUserRoleAndInvite = (user: User): User => {
  return {
    ...user,
    role: Role[user.role as unknown as keyof typeof Role],
    invite: mapApiResponseInviteToUserInvite(user.invite),
  };
};

const isAddUserPayload = (
  p:
    | Pick<AddUserFormPayload, 'formFields'>
    | Pick<UpdateUserFormPayload, 'formFields'>
): p is AddUserFormPayload => {
  return !!(p as AddUserFormPayload).formFields.email;
};

const isUpdateUserPayload = (
  p:
    | Pick<AddUserFormPayload, 'formFields'>
    | Pick<UpdateUserFormPayload, 'formFields'>
): p is UpdateUserFormPayload => {
  return !!(p as UpdateUserFormPayload).formFields.status;
};

const mapUserPreferences = (formFields: UserFormFields) =>
  formFields.locale || formFields.timeZone
    ? {
        locale: formFields.locale,
        timeZone: formFields.timeZone,
      }
    : undefined;

export const mapUserFormToApiRequest = (
  payload: AddUserFormPayload | UpdateUserFormPayload,
  entity?: Partial<User>
): UserApiRequestPayload => {
  let result = {
    role: Object.keys(Role)[
      Object.values(Role).indexOf(payload.formFields.role)
    ],
  } as UserApiRequestPayload;
  if (isAddUserPayload(payload)) {
    result = {
      ...result,
      email: payload.formFields.email,
      firstName: payload.formFields.firstName,
      lastName: payload.formFields.lastName,
      phoneNumber: payload.formFields.phoneNumber || undefined,
    };
  } else if (isUpdateUserPayload(payload)) {
    result = {
      ...result,
      organizationId:
        payload.params.organizationsId || entity?.organization?.id || null,
      fleetId: payload.formFields.fleet?.id || entity?.fleet?.id || null,
      status: uppercaseLiteral(payload.formFields.status),
      userPreferences: mapUserPreferences(payload.formFields),
    };
  }

  return result;
};
